import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { focusNextElement } from "@/shared/utils/keyboardNavigation";
import { toTitleCaseUi } from "@/shared/utils/titleCase";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";

import type { BaseInputProps, InputOption } from "@/shared/types";
import { inputClassName } from "./TextInput";
import { WrapperInput } from "./WrapperInput";

type SelectInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
> = BaseInputProps<TFieldValues, TName> & {
  options: InputOption<TValue>[];
  searchable?: boolean;
  searchPlaceholder?: string;
  hideChevron?: boolean;
};

export function SelectInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
>({
  control,
  name,
  label,
  placeholder = "Select option",
  description,
  disabled,
  required,
  className,
  options,
  searchable = false,
  searchPlaceholder = "Search options",
  hideChevron = false,
}: SelectInputProps<TFieldValues, TName, TValue>) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const inputClasses = getThemeComponentGroup("input");
  const selectClasses = getThemeComponentGroup("select");
  const placeholderText = toTitleCaseUi(placeholder);
  const searchPlaceholderText = toTitleCaseUi(searchPlaceholder);

  useEffect(() => {
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    // Capture phase keeps outside-close reliable even if parent handlers stop propagation.
    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
  }, []);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setHighlightedIndex(-1);
      return;
    }

    if (searchable) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open, searchable]);

  // Scroll the highlighted option into view while navigating with the keyboard.
  useEffect(() => {
    if (!open || highlightedIndex < 0) {
      return;
    }

    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [open, highlightedIndex]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedOption = options.find((option) => option.value === field.value);
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const filteredOptions = normalizedSearch
          ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
          : options;
        const displayValue = open && searchable ? searchTerm : (selectedOption?.label ?? "");
        const resolvedTestId = field.name;

        const commitSelection = (option: InputOption<TValue>) => {
          field.onChange(option.value);
          setSearchTerm("");
          setHighlightedIndex(-1);
          setOpen(false);
        };

        const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
          if (!searchable) return;

          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!open) {
              setOpen(true);
              setHighlightedIndex(filteredOptions.length ? 0 : -1);
              return;
            }

            setHighlightedIndex((current) => {
              if (!filteredOptions.length) return -1;
              return current < filteredOptions.length - 1 ? current + 1 : 0;
            });
            return;
          }

          if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) {
              setOpen(true);
              setHighlightedIndex(filteredOptions.length ? filteredOptions.length - 1 : -1);
              return;
            }

            setHighlightedIndex((current) => {
              if (!filteredOptions.length) return -1;
              return current > 0 ? current - 1 : filteredOptions.length - 1;
            });
            return;
          }

          if (event.key === "Enter") {
            if (!open) {
              setOpen(true);
              setHighlightedIndex(filteredOptions.length ? 0 : -1);
              return;
            }

            event.preventDefault();

            const optionToSelect =
              highlightedIndex >= 0 ? filteredOptions[highlightedIndex] : filteredOptions[0];

            if (optionToSelect && !optionToSelect.disabled) {
              commitSelection(optionToSelect);
            }

            return;
          }

          if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            return;
          }
        };

        const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!open) {
              setOpen(true);
              setHighlightedIndex(filteredOptions.length ? 0 : -1);
              return;
            }

            setHighlightedIndex((current) => {
              if (!filteredOptions.length) return -1;
              return current < filteredOptions.length - 1 ? current + 1 : 0;
            });
            return;
          }

          if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) {
              setOpen(true);
              setHighlightedIndex(filteredOptions.length ? filteredOptions.length - 1 : -1);
              return;
            }

            setHighlightedIndex((current) => {
              if (!filteredOptions.length) return -1;
              return current > 0 ? current - 1 : filteredOptions.length - 1;
            });
            return;
          }

          if (event.key === "Enter" || event.key === " ") {
            if (open) {
              event.preventDefault();
              const optionToSelect =
                highlightedIndex >= 0 ? filteredOptions[highlightedIndex] : filteredOptions[0];

              if (optionToSelect && !optionToSelect.disabled) {
                commitSelection(optionToSelect);
              }
              return;
            }

            // Value already chosen — move to the next field instead of reopening the list.
            if (selectedOption) {
              event.preventDefault();
              focusNextElement(event.currentTarget);
            }
            return;
          }

          if (event.key === "Escape" && open) {
            event.preventDefault();
            setOpen(false);
          }
        };

        return (
          <WrapperInput
            label={label}
            error={fieldState.error?.message}
            description={description}
            required={required}
            className={className}
          >
            <div ref={containerRef} className={clsx("relative", open && "z-50")}>
              <div
                className={clsx(
                  inputClassName,
                  "flex items-center justify-between gap-3 text-left",
                  getThemeClass(
                    selectClasses.borderColor || inputClasses.borderColor,
                    "border-theme-border-strong"
                  ),
                  getThemeClass(selectClasses.bgColor || inputClasses.bgColor, "bg-theme-surface"),
                  getThemeClass(
                    selectClasses.textColor || inputClasses.textColor,
                    "text-theme-text"
                  ),
                  getThemeClass(inputClasses.focusBorderColor, "focus-within:border-theme-primary"),
                  "focus-within:ring-2",
                  getThemeClass(inputClasses.focusRingColor, "focus-within:ring-theme-primary/10"),
                  inputClasses.base,
                  selectClasses.trigger,
                  fieldState.error &&
                    clsx(
                      inputClasses.errorBorderColor || "border-theme-danger-bright",
                      "focus-within:ring-theme-danger-bright/10"
                    )
                )}
              >
                {searchable ? (
                  <input
                    ref={inputRef}
                    id={resolvedTestId}
                    type="text"
                    value={displayValue}
                    disabled={disabled}
                    placeholder={open ? searchPlaceholderText : placeholderText}
                    onClick={() => setOpen(true)}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      setHighlightedIndex(0);
                      if (!open) {
                        setOpen(true);
                      }
                    }}
                    onKeyDown={handleSearchKeyDown}
                    onBlur={() => {
                      window.setTimeout(() => {
                        setSearchTerm("");
                      }, 100);
                    }}
                    className={clsx(
                      "min-w-0 flex-1 truncate bg-transparent text-[13px] outline-none",
                      selectedOption || searchTerm
                        ? getThemeClass(
                            selectClasses.textColor || inputClasses.textColor,
                            "text-theme-text"
                          )
                        : getThemeClass(
                            selectClasses.placeholderColor || inputClasses.placeholderColor,
                            "text-theme-text-placeholder"
                          ),
                      getThemeClass(
                        selectClasses.placeholderColor || inputClasses.placeholderColor,
                        "placeholder:text-theme-text-placeholder"
                      )
                    )}
                  />
                ) : (
                  <button
                    type="button"
                    id={resolvedTestId}
                    disabled={disabled}
                    onClick={() => setOpen((current) => !current)}
                    onKeyDown={handleTriggerKeyDown}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span
                      className={clsx(
                        "block truncate",
                        selectedOption
                          ? getThemeClass(
                              selectClasses.textColor || inputClasses.textColor,
                              "text-theme-text"
                            )
                          : getThemeClass(
                              selectClasses.placeholderColor || inputClasses.placeholderColor,
                              "text-theme-text-placeholder"
                            )
                      )}
                    >
                      {selectedOption?.label ?? placeholderText}
                    </span>
                  </button>
                )}
                {!hideChevron ? (
                  <ChevronDown
                    className={clsx(
                      "h-4 w-4 shrink-0 transition",
                      getThemeClass(
                        selectClasses.placeholderColor || inputClasses.placeholderColor,
                        "text-theme-text-soft"
                      ),
                      open && "rotate-180"
                    )}
                    onClick={() => {
                      if (disabled) return;
                      setOpen((current) => !current);
                      if (!open && searchable) {
                        requestAnimationFrame(() => {
                          inputRef.current?.focus();
                        });
                      }
                    }}
                  />
                ) : null}
              </div>

              {open ? (
                <div
                  className={clsx(
                    "absolute z-50 mt-2 max-h-56 w-full overflow-auto thin-scrollbar rounded-lg border shadow-lg",
                    getThemeClass(
                      selectClasses.borderColor || inputClasses.borderColor,
                      "border-theme-border"
                    ),
                    getThemeClass(
                      selectClasses.bgColor || inputClasses.bgColor,
                      "bg-theme-surface"
                    ),
                    selectClasses.menu
                  )}
                >
                  {filteredOptions.length ? (
                    filteredOptions.map((option, index) => (
                      <button
                        key={String(option.value)}
                        ref={(element) => {
                          optionRefs.current[index] = element;
                        }}
                        type="button"
                        disabled={option.disabled}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          if (option.disabled) return;
                          commitSelection(option);
                        }}
                        onClick={() => {
                          // Keyboard activation (Tab + Enter/Space) only fires "click", not "mousedown".
                          if (option.disabled) return;
                          commitSelection(option);
                        }}
                        onMouseEnter={() =>
                          setHighlightedIndex(
                            filteredOptions.findIndex((entry) => entry.value === option.value)
                          )
                        }
                        onFocus={() =>
                          setHighlightedIndex(
                            filteredOptions.findIndex((entry) => entry.value === option.value)
                          )
                        }
                        className={clsx(
                          "block w-full px-4 py-3 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50",
                          getThemeClass(
                            selectClasses.textColor || inputClasses.textColor,
                            "text-theme-text"
                          ),
                          getThemeClass(
                            selectClasses.optionHoverBgColor,
                            "hover:bg-theme-surface-soft"
                          ),
                          highlightedIndex >= 0 &&
                            filteredOptions[highlightedIndex]?.value === option.value &&
                            getThemeClass(
                              selectClasses.optionHoverBgColor,
                              "bg-theme-surface-soft"
                            ),
                          selectClasses.option
                        )}
                      >
                        {option.label}
                      </button>
                    ))
                  ) : (
                    <div
                      className={clsx(
                        "px-4 py-3 text-sm",
                        getThemeClass(
                          selectClasses.placeholderColor || inputClasses.placeholderColor,
                          "text-theme-text-soft"
                        )
                      )}
                    >
                      No matching options found
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </WrapperInput>
        );
      }}
    />
  );
}
